<script setup>
import { computed, reactive } from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import IspDataRow from './IspDataRow.vue';
import IspStatusBadge from './IspStatusBadge.vue';
import { getFinancialBadgeClasses } from './useIspStatusStyles';
import ErpCommandsAPI from 'dashboard/api/erpCommands'; // GK-HUB: ERP command relay

const { t } = useI18n();
const route = useRoute();
const currentChat = useMapGetter('getSelectedChat');
const attrs = computed(() => currentChat.value?.custom_attributes || {});

const financialClasses = computed(() =>
  getFinancialBadgeClasses(attrs.value.situacao_financeira)
);

const hasAtraso = computed(() => {
  const b = Number(attrs.value.boletos_em_atraso);
  return b > 0;
});

const formatCurrency = val => {
  if (val == null || val === '') return '—';
  const num = Number(val);
  if (Number.isNaN(num)) return val;
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const valorAtrasoFormatted = computed(() =>
  formatCurrency(attrs.value.valor_em_atraso)
);

const proximoVencFormatted = computed(() => {
  const data = attrs.value.proximo_vencimento;
  const valor = attrs.value.proximo_vencimento_valor;
  if (!data && !valor) return null;
  return { data: data || '—', valor: valor ? formatCurrency(valor) : '' };
});

// GK-HUB: Parse boletos_detalhes — format: "id:ref:venc:valor:dias|id:ref:venc:valor:dias"
const boletos = computed(() => {
  const raw = attrs.value.boletos_detalhes;
  if (!raw) return [];
  return String(raw)
    .split('|')
    .map((seg, idx) => {
      const trimmed = seg.trim();
      if (!trimmed) return null;
      // Limit to 5 parts to handle colons in data gracefully
      const parts = trimmed.split(':');
      if (parts.length < 4) return null;
      const id = (parts[0] || '').trim();
      const referencia = (parts[1] || '').trim();
      const vencimento = (parts[2] || '').trim();
      const valorRaw = (parts[3] || '').trim();
      const dias = (parts[4] || '').trim();
      return {
        boletoKey: id || `boleto-${idx}`,
        id,
        ref: referencia,
        vencimento,
        valor: formatCurrency(valorRaw),
        dias,
      };
    })
    .filter(Boolean);
});

const hasBoletos = computed(() => boletos.value.length > 0);

// Track loading + cooldown per boleto (keyed by boletoKey)
const boletoState = reactive({});
// States: null (idle) | 'sending' | 'sent' | 'error'

const getBoletoState = key => boletoState[key] || null;

const COOLDOWN_MS = 5000;

const enviarSegundaVia = async boleto => {
  const accountId = route.params.accountId;
  const conversationId = currentChat.value?.id;
  if (!accountId || !conversationId) return;
  if (boletoState[boleto.boletoKey] === 'sending') return;

  boletoState[boleto.boletoKey] = 'sending';
  try {
    await ErpCommandsAPI.send(accountId, conversationId, '/segunda_via', {
      boleto_id: boleto.id,
      boleto_ref: boleto.ref,
    });
    boletoState[boleto.boletoKey] = 'sent';
    const refLabel = boleto.ref || `#${boleto.id}`;
    useAlert(
      `${t('CONVERSATION_SIDEBAR.ACCORDION.ISP_BOLETO_SEGUNDA_VIA_OK')} (${refLabel})`
    );
    // Cooldown: reset to idle after delay
    setTimeout(() => {
      if (boletoState[boleto.boletoKey] === 'sent') {
        boletoState[boleto.boletoKey] = null;
      }
    }, COOLDOWN_MS);
  } catch (err) {
    boletoState[boleto.boletoKey] = 'error';
    const status = err?.response?.status;
    const errorCode = err?.response?.data?.error_code;
    let msg = t('CONVERSATION_SIDEBAR.ACCORDION.ISP_BOLETO_SEGUNDA_VIA_ERRO');
    if (status === 503 || errorCode === 'erp_not_configured') {
      msg = t('CONVERSATION_SIDEBAR.ACCORDION.ISP_ERP_NAO_CONFIGURADO');
    }
    useAlert(msg);
    // Reset error state after delay so user can retry
    setTimeout(() => {
      if (boletoState[boleto.boletoKey] === 'error') {
        boletoState[boleto.boletoKey] = null;
      }
    }, 3000);
  }
};

const isDisabled = key => {
  const s = getBoletoState(key);
  return s === 'sending' || s === 'sent';
};

const buttonLabel = key => {
  const s = getBoletoState(key);
  if (s === 'sending')
    return t('CONVERSATION_SIDEBAR.ACCORDION.ISP_BOLETO_ENVIANDO');
  if (s === 'sent')
    return t('CONVERSATION_SIDEBAR.ACCORDION.ISP_BOLETO_ENVIADO');
  return t('CONVERSATION_SIDEBAR.ACCORDION.ISP_BOLETO_SEGUNDA_VIA');
};

const buttonClass = key => {
  const s = getBoletoState(key);
  if (s === 'sent') return 'bg-n-teal-3 text-n-teal-11';
  if (s === 'error') return 'bg-n-ruby-3 text-n-ruby-11';
  return 'bg-n-blue-3 text-n-blue-11 hover:bg-n-blue-4 active:bg-n-blue-5';
};
// GK-HUB: end boletos detalhes
</script>

<template>
  <div class="flex flex-col px-4 py-1">
    <!-- Situação badge -->
    <IspDataRow label="Situação">
      <IspStatusBadge
        :label="attrs.situacao_financeira"
        :bg="financialClasses.bg"
        :text="financialClasses.text"
        :dot="financialClasses.dot"
        :ring="financialClasses.ring"
      />
    </IspDataRow>

    <!-- Boletos em atraso -->
    <IspDataRow label="Boletos em atraso">
      <span
        class="text-xs"
        :class="
          attrs.boletos_em_atraso != null
            ? hasAtraso
              ? 'font-semibold text-n-ruby-11'
              : 'font-semibold text-n-teal-11'
            : 'font-medium text-n-slate-12'
        "
      >
        {{ attrs.boletos_em_atraso ?? '—' }}
      </span>
    </IspDataRow>

    <!-- Valor em atraso -->
    <IspDataRow label="Valor em atraso">
      <span
        class="text-xs"
        :class="
          attrs.valor_em_atraso != null
            ? hasAtraso
              ? 'font-semibold text-n-ruby-11'
              : 'font-semibold text-n-teal-11'
            : 'font-medium text-n-slate-12'
        "
      >
        {{ valorAtrasoFormatted }}
      </span>
    </IspDataRow>

    <!-- Meses em atraso -->
    <IspDataRow label="Meses em atraso">
      <span
        class="text-xs"
        :class="
          attrs.meses_em_atraso
            ? hasAtraso
              ? 'font-semibold text-n-ruby-11'
              : 'font-medium text-n-slate-11'
            : 'font-medium text-n-slate-12'
        "
      >
        {{ attrs.meses_em_atraso || '—' }}
      </span>
    </IspDataRow>

    <!-- Dias de atraso -->
    <IspDataRow label="Dias de atraso">
      <span
        class="text-xs"
        :class="
          attrs.dias_atraso != null
            ? hasAtraso
              ? 'font-semibold text-n-ruby-11'
              : 'font-semibold text-n-teal-11'
            : 'font-medium text-n-slate-12'
        "
      >
        {{ attrs.dias_atraso ?? '—' }}
      </span>
    </IspDataRow>

    <!-- Próximo vencimento - card destaque -->
    <div
      v-if="proximoVencFormatted"
      class="mt-2 mb-1 rounded-lg border border-n-weak bg-n-slate-2 px-3 py-2.5"
    >
      <div class="text-[10px] text-n-slate-10 uppercase tracking-wider mb-1.5">
        {{ $t('CONVERSATION_SIDEBAR.ACCORDION.ISP_PROXIMO_VENCIMENTO') }}
      </div>
      <div class="flex items-center justify-between">
        <span class="text-xs font-medium text-n-slate-11">
          {{ proximoVencFormatted.data }}
        </span>
        <span
          v-if="proximoVencFormatted.valor"
          class="text-sm font-bold text-n-slate-12"
        >
          {{ proximoVencFormatted.valor }}
        </span>
      </div>
    </div>

    <!-- GK-HUB: Boletos em atraso — detail card with "Enviar 2ª via" per boleto -->
    <div
      v-if="hasBoletos"
      class="mt-2 mb-1 rounded-lg border border-n-ruby-6/30 bg-n-ruby-2/30 overflow-hidden"
    >
      <!-- Header -->
      <div class="px-3 py-1.5 border-b border-n-ruby-6/20">
        <span
          class="text-[10px] text-n-ruby-11 uppercase tracking-wider font-semibold"
        >
          {{ $t('CONVERSATION_SIDEBAR.ACCORDION.ISP_BOLETOS_TITLE') }}
        </span>
      </div>
      <!-- Boleto rows -->
      <div class="flex flex-col">
        <div
          v-for="(b, idx) in boletos"
          :key="b.boletoKey"
          class="px-3 py-2"
          :class="idx < boletos.length - 1 ? 'border-b border-n-ruby-6/15' : ''"
        >
          <!-- Row 1: ref + valor -->
          <div class="flex items-center justify-between mb-1">
            <span class="font-mono text-[10px] text-n-slate-10">
              {{ b.ref || '#' + b.id }}
            </span>
            <span class="text-xs font-bold text-n-ruby-11">
              {{ b.valor }}
            </span>
          </div>
          <!-- Row 2: vencimento + dias atraso -->
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-[10px] text-n-slate-10">
              {{
                $t('CONVERSATION_SIDEBAR.ACCORDION.ISP_BOLETO_VENC', {
                  date: b.vencimento,
                })
              }}
            </span>
            <span v-if="b.dias" class="text-[10px] text-n-ruby-10 font-medium">
              {{
                $t('CONVERSATION_SIDEBAR.ACCORDION.ISP_BOLETO_DIAS_ATRASO', {
                  days: b.dias,
                })
              }}
            </span>
          </div>
          <!-- Enviar 2ª via button -->
          <button
            class="w-full text-[10px] font-semibold py-1 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            :class="buttonClass(b.boletoKey)"
            :disabled="isDisabled(b.boletoKey)"
            @click="enviarSegundaVia(b)"
          >
            {{ buttonLabel(b.boletoKey) }}
          </button>
        </div>
      </div>
    </div>
    <!-- GK-HUB: end boletos detalhes -->
  </div>
</template>
